import React, { useState } from "react";
import { faqData } from "@data/faqData";
import SEO from "@components/SEO";
import {
  createFAQStructuredData,
  createBreadcrumbStructuredData,
} from "../utils/structuredData";

const FAQPage: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");

  // Get unique categories
  const categories = [
    "All",
    ...Array.from(new Set(faqData.map((faq) => faq.category))),
  ];

  // Filter FAQs by selected category
  const filteredFAQs =
    selectedCategory === "All"
      ? faqData
      : faqData.filter((faq) => faq.category === selectedCategory);

  const toggleItem = (id: number) => {
    setOpenItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (openItems.length === filteredFAQs.length) {
      setOpenItems([]);
    } else {
      setOpenItems(filteredFAQs.map((faq) => faq.id));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO
        title="New Zealand Tour FAQs | Booking, Packages & Vehicles"
        description="Answers to common questions about Ikhlas Tours New Zealand packages — booking, payment, cancellations, vehicle rentals, Muslim-friendly options and what to pack."
        path="/faq"
        structuredData={[
          createFAQStructuredData(faqData),
          createBreadcrumbStructuredData([
            { name: "Home", url: "/" },
            { name: "FAQ", url: "/faq" },
          ]),
        ]}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find answers to common questions about our tours and services in New
            Zealand
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() =>
                  setSelectedCategory(
                    category === selectedCategory ? "All" : category
                  )
                }
                className={`cursor-pointer px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? "bg-primary text-white"
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Toggle All Button */}
        <div className="flex justify-end mb-6">
          <button
            onClick={toggleAll}
            className="cursor-pointer text-primary hover:text-primaryText font-medium transition-colors duration-300"
          >
            {openItems.length === filteredFAQs.length
              ? "Collapse All"
              : "Expand All"}
          </button>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {filteredFAQs.map((faq) => (
            <div
              key={faq.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md"
            >
              <button
                onClick={() => toggleItem(faq.id)}
                className="cursor-pointer w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-300"
              >
                <div className="flex-1 pr-4">
                  <h3 className="text-lg font-semibold text-primaryText">
                    {faq.question}
                  </h3>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {faq.category}
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
                      openItems.includes(faq.id) ? "transform rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              {/* Answer Panel */}
              <div
                className={`transition-all duration-300 ease-in-out ${
                  openItems.includes(faq.id)
                    ? "max-h-96 opacity-100"
                    : "max-h-0 opacity-0 overflow-hidden"
                }`}
              >
                <div className="px-6 pb-4 pt-2">
                  <div className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-16 bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <h2 className="text-2xl font-bold text-primaryText mb-4">
            Still have questions?
          </h2>
          <p className="text-gray-600 mb-6">
            Can't find the answer you're looking for? Our team is here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-block bg-primary text-white px-6 py-3 rounded-md font-semibold hover:bg-opacity-90 transition-all duration-300"
            >
              Contact Us
            </a>
            <button
              onClick={() =>
                window.open(
                  "https://wa.me/64221841601?text=Hi! I have a question about your tours.",
                  "_blank"
                )
              }
              className="inline-block bg-whatsapp cursor-pointer text-white px-6 py-3 rounded-md font-semibold hover:bg-green-600 transition-colors"
            >
              WhatsApp Us
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
